import React from 'react';
import { BookOpen, Database, Calculator, Settings2, AlertTriangle, RefreshCw, Target } from 'lucide-react';
import { Card, CardHeader, CardContent } from '@components/Card';
import { METRIC_DESCRIPTIONS } from './metricDescriptions';

// Ordem de exibição dos indicadores na seção "Indicadores" (espelha
// ERPApi/docs/funcionalidades/estoque.md, análise de estoque — metricDescriptions.ts é a fonte única).
const METRIC_ORDER = [
  'avg_daily_demand',
  'turnover_365d',
  'turnover_90d_annualized',
  'turnover_cost_365d',
  'coverage_days',
  'days_since_last_sale',
  'slow_moving_class',
  'abc_class',
  'xyz_class',
  'seasonal_index',
  'lead_time_avg_days',
  'safety_stock_qty',
  'reorder_point_qty',
  'suggested_min_qty',
  'suggested_max_qty',
  'suggested_order_qty',
  'on_order_qty',
  'excess_stock',
  'stockout_risk',
  'stockout_days_365d',
  'forecast_cycle_qty',
  'confidence',
];

const Section: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode }> = ({ icon, title, children }) => (
  <Card>
    <CardHeader title={<span className="flex items-center gap-2">{icon}{title}</span>} />
    <CardContent>
      <div className="text-sm text-slate-600 space-y-3 leading-relaxed">{children}</div>
    </CardContent>
  </Card>
);

/**
 * Sub-aba "Conceitos": documentação dos indicadores e das regras da análise de
 * estoque, espelhando ERPApi/docs/funcionalidades/estoque.md.
 */
export const StockAnalyticsConceptsView: React.FC = () => (
  <div className="space-y-5">
    <Section icon={<BookOpen size={18} className="text-emerald-600" />} title="Visão geral e escopo">
      <p>
        Os indicadores desta análise são calculados <strong>diariamente</strong> pelo processo batch{' '}
        <span className="font-mono text-xs">stock_analytics</span> e gravados em tabelas dedicadas — os relatórios
        nunca varrem as tabelas operacionais em tempo real, evitando concorrência com a operação.
      </p>
      <p>
        <strong>Escopo:</strong> produtos acabados ativos. Ficam <strong>fora da análise</strong> os produtos com a
        flag <em>"Fora da análise de estoque"</em> no cadastro:
      </p>
      <ul className="list-disc pl-5 space-y-1">
        <li><strong>Outlet</strong> (SKU iniciando com OUT): peças com avaria — devolvidas por clientes ou avariadas na logística. Venda pontual, sem reposição.</li>
        <li><strong>Cabides e kits de cabides</strong>: fabricados internamente sob demanda; os materiais ainda não são controlados.</li>
        <li><strong>Iniciais</strong> (personalização/bordado): produzidas sob demanda.</li>
      </ul>
      <p>
        A flag pode ser alterada no cadastro do produto (aba Controle de Estoque). A cada execução, o batch remove das
        análises os produtos que saíram do escopo.
      </p>
    </Section>

    <Section icon={<Database size={18} className="text-blue-600" />} title="Definição de demanda e fontes de dados">
      <p>
        <strong>Demanda líquida</strong> = quantidade vendida − devoluções, agrupada pelo <strong>mês da data do
        pedido de venda</strong> (quando o cliente quis o produto, não quando a logística movimentou), excluindo
        pedidos <em>Cancelados</em> e <em>Desmembrados</em> (os filhos do desmembramento já contam).
      </p>
      <ul className="list-disc pl-5 space-y-1">
        <li><strong>Brindes contam na demanda</strong> (consomem estoque e precisam ser repostos), mas não contam na receita.</li>
        <li><strong>Estoque médio e dias em ruptura</strong> vêm do snapshot diário de estoque (disponível desde 26/05/2023).</li>
        <li><strong>Lead time</strong> vem do histórico real: dias entre o pedido de compra e o recebimento confirmado.</li>
        <li>
          <strong>Fornecedor do produto</strong>: correlação via <em>Tabela de Preços de Fornecedores</em>{' '}
          (ProviderProductPrice) — vale o relacionamento de preço mais recente com fornecedor ativo. O campo
          "fornecedor padrão" antigo do cadastro é legado e não é usado.
        </li>
        <li><strong>Quantidade em pedido</strong>: pedidos de compra abertos (Em aberto/Em andamento) menos o que já foi recebido.</li>
      </ul>
    </Section>

    <Section icon={<Calculator size={18} className="text-purple-600" />} title="Indicadores — fórmula e racional">
      <p className="text-xs text-slate-400">
        Cada produto guarda os valores intermediários do seu próprio cálculo — expanda a linha na aba Indicadores e
        veja o painel "Como foi calculado".
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {METRIC_ORDER.map((key) => {
          const m = METRIC_DESCRIPTIONS[key];
          if (!m) return null;
          return (
            <div key={key} className="bg-slate-50 rounded-lg border border-slate-200 p-3">
              <p className="text-sm font-semibold text-slate-800">{m.label}</p>
              <p className="text-xs text-slate-600 mt-0.5">{m.short}</p>
              <div className="mt-2 px-2 py-1.5 bg-white rounded border border-slate-100">
                <p className="text-[11px] font-mono text-slate-700 whitespace-pre-wrap">{m.formula}</p>
              </div>
              <p className="text-xs text-slate-500 mt-2">{m.rationale}</p>
            </div>
          );
        })}
      </div>
    </Section>

    <Section icon={<Target size={18} className="text-emerald-600" />} title="Política de reposição — exemplo numérico">
      <p>
        Política de <strong>revisão periódica (mín/máx)</strong>: o mínimo dispara a compra e cobre a demanda até a
        entrega; o máximo cobre até a chegada do pedido <em>seguinte</em>.
      </p>
      <div className="bg-slate-50 rounded-lg border border-slate-200 p-4 text-xs font-mono text-slate-700 space-y-1">
        <p>Demanda média d̄ = 2 un/dia · desvio σd = 1 · lead time LT = 10 dias · ciclo de revisão = 30 dias · nível de serviço 95% (Z = 1,645)</p>
        <p>Estoque de segurança SS = 1,645 × 1 × √10 ≈ 5,2 un</p>
        <p>Ponto de pedido ROP = 2 × 10 + 5,2 = 25,2 → <strong>Mínimo sugerido = 26</strong></p>
        <p>Máximo = 2 × (10 + 30) + 5,2 = 85,2 → <strong>Máximo sugerido = 86</strong></p>
        <p>Com 20 disponíveis + 10 em pedido: <strong>Compra sugerida = 86 − 30 = 56 un</strong></p>
      </div>
      <p className="text-xs text-slate-500">
        Em meses de pico a demanda prevista é multiplicada pelo índice sazonal do período coberto, elevando mín/máx
        proporcionalmente.
      </p>
    </Section>

    <Section icon={<Settings2 size={18} className="text-slate-600" />} title="Parâmetros de cálculo">
      <p>
        Todos os limiares e janelas são <strong>configuráveis</strong> (botão "Parâmetros", restrito a admin) e valem
        na próxima execução do batch: nível de serviço (95%), ciclo de revisão (30d), limiares de aging (90/180/365d),
        janelas de demanda/ABC (12m), limiares ABC (80/95%) e XYZ (0,5/1,0), cobertura de excesso (180d), histórico
        mínimo para sazonalidade (24m), janela e amostras mínimas de lead time, e lead time padrão (30d).
      </p>
    </Section>

    <Section icon={<RefreshCw size={18} className="text-blue-600" />} title="Processo batch e retomada">
      <ul className="list-disc pl-5 space-y-1">
        <li>Executa diariamente às 04:00; pode ser reexecutado em Configuração → Operações → Execução de Processos.</li>
        <li>Processa produto a produto: se falhar no meio, a reexecução no mesmo dia <strong>continua de onde parou</strong>.</li>
        <li>Meses fechados da série histórica nunca são recalculados — apenas a janela recente (padrão 3 meses).</li>
        <li>A cada execução, métricas de produtos fora do escopo (inativos ou flagados) são removidas do relatório.</li>
      </ul>
    </Section>

    <Section icon={<AlertTriangle size={18} className="text-amber-600" />} title="Limitações e cuidados">
      <ul className="list-disc pl-5 space-y-1">
        <li><strong>Produtos novos</strong> aparecem como "Sem histórico", sem sugestões (não há base estatística).</li>
        <li><strong>Ruptura passada deprime a demanda histórica</strong>: dias sem estoque venderam zero porque não havia produto. Produtos com muitos dias em ruptura têm demanda real maior que a calculada — valide antes de reduzir mín/máx.</li>
        <li>O snapshot diário de estoque começa em 26/05/2023 e pode ter lacunas — a "confiança" no racional considera a cobertura desses dados.</li>
        <li>Sugestões são apoio à decisão: o botão "Aplicar" exige confirmação humana e fica registrado quem aplicou.</li>
      </ul>
    </Section>
  </div>
);

export default StockAnalyticsConceptsView;
